import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Eye, EyeOff } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect") || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate(redirect);
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex flex-col items-center justify-center px-6 py-20" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <Link to="/">
            <img src="https://customer-assets.emergentagent.com/job_beyond-stuck/artifacts/4bqnieq1_Company%20Logo.jpeg" alt="Logo" className="h-14 w-14 mx-auto mb-4 rounded-sm" />
          </Link>
          <p className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-2">De Nicola Consulting Co.</p>
          <h1 className="font-lora text-2xl text-navy font-normal">Welcome Back</h1>
          <div className="w-8 h-px bg-gold mx-auto mt-4"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/60 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="w-full border border-soft-gray bg-white px-4 py-3 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
              placeholder="your@email.com"
              data-testid="login-email-input"
            />
          </div>
          <div className="relative">
            <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/60 mb-2">Password</label>
            <input
              type={showPwd ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              className="w-full border border-soft-gray bg-white px-4 py-3 pr-12 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
              placeholder="••••••••"
              data-testid="login-password-input"
            />
            <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-[38px] text-navy/40 hover:text-navy">
              {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {error && <p className="text-red-600 font-serif text-sm" data-testid="login-error">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-navy text-ivory py-3.5 text-sm tracking-[0.15em] uppercase hover:bg-navy/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
            data-testid="login-submit-btn"
          >
            {loading ? <><Loader2 size={15} className="animate-spin" /> Signing In...</> : "Sign In"}
          </button>

          <div className="text-right mt-1">
            <Link to="/forgot-password" className="text-navy/40 font-serif text-xs hover:text-navy transition-colors underline underline-offset-2" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>
        </form>

        <p className="text-center text-navy/50 font-serif text-sm mt-6">
          Don't have an account?{" "}
          <Link to="/register" className="text-navy hover:text-gold transition-colors underline" data-testid="login-register-link">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
